import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash,
  FilePdf,
  Upload,
  DownloadSimple,
  Camera,
  Warning,
  CheckCircle,
  ImageSquare,
  FileArrowUp,
  FileXls,
  PencilSimple,
} from "@phosphor-icons/react";
import { usePerms } from "@/lib/perms";
import EditAppointmentModal from "@/components/EditAppointmentModal";
import { StatusChip, STATUS_META } from "@/components/StatusChip";
import { ReceptionChip, findReceptionStatus } from "@/components/ReceptionChip";
import { useReceptionStatuses } from "@/lib/receptionStatuses";
import RelatedRejections from "@/components/RelatedRejections";
import PdfPreview from "@/components/PdfPreview";

const EMPTY_ROW = {
  br: "",
  po: "",
  sku: "",
  vpn: "",
  upc: "",
  brand: "",
  qty_expected: 0,
  qty_received: 0,
  unit_price: 0,
  rejected: false,
  rejection_reason: "",
};

export default function AppointmentDetail() {
  const { id } = useParams();
  const navRouter = useNavigate();
  const [appt, setAppt] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({}); // pid -> local changes
  const [evidenceOpen, setEvidenceOpen] = useState(null); // product id
  const [showPassPreview, setShowPassPreview] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const perms = usePerms();
  const bulkRef = useRef(null);
  const { list: receptionList } = useReceptionStatuses();

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, p] = await Promise.all([
        api.get(`/appointments/${id}`),
        api.get(`/appointments/${id}/products`),
      ]);
      setAppt(a.data);
      setProducts(p.data);
      setDirty({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [id]);

  const addRow = async () => {
    try {
      const { data } = await api.post(`/appointments/${id}/products`, EMPTY_ROW);
      setProducts((prev) => [...prev, data]);
      toast.success("Fila agregada");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateLocal = (pid, patch) => {
    setDirty((d) => ({ ...d, [pid]: { ...(d[pid] || {}), ...patch } }));
    setProducts((prev) => prev.map((p) => (p.id === pid ? { ...p, ...patch } : p)));
  };

  const saveRow = async (p) => {
    try {
      const body = {
        br: p.br || "",
        po: p.po || "",
        sku: p.sku || "",
        vpn: p.vpn || "",
        upc: p.upc || "",
        brand: p.brand || "",
        qty_expected: Number(p.qty_expected) || 0,
        qty_received: Number(p.qty_received) || 0,
        unit_price: Number(p.unit_price) || 0,
        rejected: !!p.rejected,
        rejection_reason: p.rejection_reason || "",
      };
      const { data } = await api.put(`/appointments/${id}/products/${p.id}`, body);
      setProducts((prev) => prev.map((x) => (x.id === p.id ? data : x)));
      setDirty((d) => {
        const c = { ...d };
        delete c[p.id];
        return c;
      });
      toast.success("Guardado");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteRow = async (pid) => {
    if (!window.confirm("¿Eliminar esta fila?")) return;
    try {
      await api.delete(`/appointments/${id}/products/${pid}`);
      setProducts((prev) => prev.filter((p) => p.id !== pid));
      toast.success("Eliminado");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadFile = async (path, filename) => {
    const token = localStorage.getItem("rcn_token");
    const res = await fetch(`${API}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = () =>
    downloadFile(`/appointments/${id}/pdf`, `Pase_${appt?.folio || id}.pdf`);
  const downloadExport = () =>
    downloadFile(`/appointments/${id}/export.xlsx`, `Recibo_${appt?.folio || id}.xlsx`);
  const downloadCsvTemplate = () => downloadFile(`/products/template.csv`, "template_productos.csv");

  const deleteAppointment = async () => {
    if (!appt) return;
    if (
      !window.confirm(
        `¿Eliminar la cita ${appt.folio}?\n\nEsto borrará también todos los productos capturados y su evidencia. Esta acción no se puede deshacer.`
      )
    )
      return;
    try {
      await api.delete(`/appointments/${id}`);
      toast.success(`Cita ${appt.folio} eliminada`);
      navRouter("/citas");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeStatus = async (next) => {
    try {
      const { data } = await api.put(`/appointments/${id}/status`, { status: next });
      setAppt((prev) => ({ ...prev, ...data }));
      toast.success(`Estado: ${STATUS_META[next].label}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeReception = async (next) => {
    try {
      const { data } = await api.put(`/appointments/${id}/reception-status`, { reception_status: next });
      setAppt((prev) => ({ ...prev, ...data }));
      const s = findReceptionStatus(receptionList, next);
      toast.success(`Recepción: ${s?.label || next}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const downloadXlsxTemplate = () =>
    downloadFile(`/products/template.xlsx`, "template_productos.xlsx");

  const handleBulk = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("rcn_token");
      const res = await fetch(`${API}/appointments/${id}/products/bulk`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success(`${data.inserted} filas cargadas`);
      loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading || !appt) {
    return <div className="text-white/50 text-sm">Cargando cita…</div>;
  }

  const totals = products.reduce(
    (acc, p) => {
      const line = (Number(p.qty_received) || 0) * (Number(p.unit_price) || 0);
      acc.total += line;
      acc.qty += Number(p.qty_received) || 0;
      if (p.rejected) acc.rej += 1;
      return acc;
    },
    { total: 0, qty: 0, rej: 0 }
  );

  const dt = safeDate(appt.appointment_datetime);

  return (
    <div>
      <Link
        to="/citas"
        className="inline-flex items-center gap-2 text-xs text-white/50 hover:text-white transition-colors mb-4"
      >
        <ArrowLeft size={14} /> Volver a citas
      </Link>

      {/* Header card */}
      <div className="bg-[#111111] border border-white/10 rounded-sm p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-[#0055FF] mb-1">
              Cita · Recepción
            </div>
            <div className="flex items-baseline gap-4 flex-wrap">
              <h1 className="font-mono-data font-bold text-5xl text-[#0055FF]" data-testid="detail-folio">
                {appt.folio}
              </h1>
              <div className="text-white/60 text-sm">{dt}</div>
              <StatusChip
                status={appt.status}
                testId="detail-status-chip"
                onChange={changeStatus}
              />
              <ReceptionChip
                status={appt.reception_status}
                testId="detail-reception-chip"
                onChange={changeReception}
              />
            </div>
            <div className="mt-3 grid sm:grid-cols-3 gap-6 text-sm">
              <Meta label="Proveedor" value={appt.provider_name} />
              <Meta label="Unidad" value={appt.unit_type_name} />
              <Meta
                label="Esperado"
                value={`${appt.qty_units}u · ${appt.qty_pallets}t · ${appt.qty_pieces}p`}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {perms.has("appointments:edit") && (
              <button
                onClick={() => setShowEdit(true)}
                data-testid="detail-edit-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-sm border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] text-sm font-semibold hover:bg-[#F59E0B]/20 transition-colors"
              >
                <PencilSimple size={16} weight="fill" /> Editar Pase
              </button>
            )}
            <button
              onClick={downloadExport}
              data-testid="export-xlsx-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-sm border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981] text-sm font-semibold hover:bg-[#10B981]/20 transition-colors"
            >
              <FileXls size={16} weight="fill" /> Exportar XLSX
            </button>
            <button
              onClick={() => setShowPassPreview((v) => !v)}
              data-testid="detail-preview-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-sm border border-white/15 bg-white/5 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
            >
              <FilePdf size={16} /> {showPassPreview ? "Ocultar previa" : "Vista previa"}
            </button>
            <button
              onClick={downloadPdf}
              data-testid="detail-pdf-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#0055FF] text-white text-sm font-semibold hover:brightness-110 active:scale-[0.98] transition-transform"
            >
              <FilePdf size={16} weight="fill" /> Pase PDF
            </button>
          </div>
        </div>
      </div>

      {/* PDF Preview (toggle) */}
      {showPassPreview && (
        <div className="mb-6">
          <PdfPreview endpoint={`/appointments/${appt.id}/pdf`} testId="pass-pdf-preview" />
        </div>
      )}

      {/* Edit modal */}
      {showEdit && (
        <EditAppointmentModal
          appt={appt}
          onClose={() => setShowEdit(false)}
          onSaved={(updated) => setAppt((prev) => ({ ...prev, ...updated }))}
        />
      )}

      {/* Summary Row */}
      <div className="grid sm:grid-cols-4 gap-3 mb-6">
        <MiniStat label="Productos" value={products.length} />
        <MiniStat label="Cant. recibida" value={totals.qty} />
        <MiniStat label="Total $" value={`$${totals.total.toFixed(2)}`} />
        <MiniStat label="Rechazos" value={totals.rej} tone={totals.rej > 0 ? "error" : "default"} />
      </div>

      {/* Rechazos vinculados a este folio (accordion) */}
      <RelatedRejections folio={appt.folio} />

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={addRow}
          data-testid="add-product-row"
          className="flex items-center gap-2 px-3 py-2 rounded-sm bg-[#0055FF] text-white text-xs font-semibold hover:brightness-110 transition-transform active:scale-[0.98]"
        >
          <Plus size={14} /> Agregar fila
        </button>
        <button
          onClick={downloadCsvTemplate}
          data-testid="download-csv-template"
          className="flex items-center gap-2 px-3 py-2 rounded-sm border border-white/10 text-xs text-white/70 hover:bg-white/5 hover:text-white transition-colors"
        >
          <DownloadSimple size={14} /> Template CSV
        </button>
        <button
          onClick={downloadXlsxTemplate}
          data-testid="download-xlsx-template"
          className="flex items-center gap-2 px-3 py-2 rounded-sm border border-white/10 text-xs text-white/70 hover:bg-white/5 hover:text-white transition-colors"
        >
          <DownloadSimple size={14} /> Template XLSX
        </button>
        <button
          onClick={() => bulkRef.current?.click()}
          data-testid="bulk-upload-btn"
          className="flex items-center gap-2 px-3 py-2 rounded-sm border border-[#0055FF]/40 bg-[#0055FF]/10 text-xs text-[#0055FF] hover:bg-[#0055FF]/20 transition-colors"
        >
          <Upload size={14} /> Carga Masiva
        </button>
        <input
          ref={bulkRef}
          type="file"
          accept=".csv,.xlsx"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleBulk(f);
            e.target.value = "";
          }}
        />
      </div>

      {/* Products table */}
      <div className="bg-[#111111] border border-white/10 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[9px] font-mono-data uppercase tracking-widest text-white/40">
              <tr className="border-b border-white/10 bg-black/40">
                <th className="text-left px-3 py-2">BR</th>
                <th className="text-left px-3 py-2">PO</th>
                <th className="text-left px-3 py-2">SKU</th>
                <th className="text-left px-3 py-2">VPN</th>
                <th className="text-left px-3 py-2">UPC</th>
                <th className="text-left px-3 py-2">Marca</th>
                <th className="text-right px-3 py-2">QTY Esp.</th>
                <th className="text-right px-3 py-2">QTY Rec.</th>
                <th className="text-right px-3 py-2">Faltantes</th>
                <th className="text-right px-3 py-2">$ Unit</th>
                <th className="text-right px-3 py-2">Total</th>
                <th className="text-center px-3 py-2">Rechazo</th>
                <th className="text-right px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={11} className="p-8 text-center">
                    <div className="border border-dashed border-white/10 rounded-sm p-6 max-w-md mx-auto">
                      <FileArrowUp
                        size={32}
                        className="text-white/20 mx-auto mb-2"
                        weight="duotone"
                      />
                      <div className="text-white/60">Aún no hay productos capturados</div>
                      <div className="text-white/40 text-[11px] mt-1">
                        Usa &quot;Agregar fila&quot; o &quot;Carga Masiva&quot; para poblar la mercancía recibida.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                products.map((p) => (
                  <React.Fragment key={p.id}>
                    <tr
                      data-testid="product-row"
                      className={`border-b border-white/5 ${
                        p.rejected ? "bg-red-900/15 border-l-2 border-l-[#FF3B30]" : ""
                      }`}
                    >
                      <Cell
                        v={p.br}
                        onChange={(v) => updateLocal(p.id, { br: v })}
                        onBlur={() => (dirty[p.id] ? saveRow(products.find((x) => x.id === p.id)) : null)}
                      />
                      <Cell
                        v={p.po}
                        onChange={(v) => updateLocal(p.id, { po: v })}
                        onBlur={() => dirty[p.id] && saveRow(p)}
                      />
                      <Cell
                        v={p.sku}
                        onChange={(v) => updateLocal(p.id, { sku: v })}
                        onBlur={() => dirty[p.id] && saveRow(p)}
                      />
                      <Cell
                        v={p.vpn}
                        onChange={(v) => updateLocal(p.id, { vpn: v })}
                        onBlur={() => dirty[p.id] && saveRow(p)}
                      />
                      <Cell
                        v={p.upc}
                        onChange={(v) => updateLocal(p.id, { upc: v })}
                        onBlur={() => dirty[p.id] && saveRow(p)}
                      />
                      <Cell
                        v={p.brand}
                        onChange={(v) => updateLocal(p.id, { brand: v })}
                        onBlur={() => dirty[p.id] && saveRow(p)}
                      />
                      <Cell
                        v={p.qty_expected}
                        onChange={(v) => updateLocal(p.id, { qty_expected: v })}
                        onBlur={() => dirty[p.id] && saveRow(p)}
                        type="number"
                        alignRight
                      />
                      <Cell
                        v={p.qty_received}
                        onChange={(v) => updateLocal(p.id, { qty_received: v })}
                        onBlur={() => dirty[p.id] && saveRow(p)}
                        type="number"
                        alignRight
                      />
                      <td className="px-3 py-2 text-right font-mono-data text-[#F59E0B]">
                        {Math.max((Number(p.qty_expected) || 0) - (Number(p.qty_received) || 0), 0)}
                      </td>
                      <Cell
                        v={p.unit_price}
                        onChange={(v) => updateLocal(p.id, { unit_price: v })}
                        onBlur={() => dirty[p.id] && saveRow(p)}
                        type="number"
                        alignRight
                      />
                      <td className="px-3 py-2 text-right font-mono-data font-bold text-white">
                        ${((Number(p.qty_received) || 0) * (Number(p.unit_price) || 0)).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <label className="inline-flex items-center justify-center cursor-pointer">
                          <input
                            data-testid="rejected-checkbox"
                            type="checkbox"
                            checked={!!p.rejected}
                            onChange={(e) => {
                              updateLocal(p.id, { rejected: e.target.checked });
                              setTimeout(() => saveRow({ ...p, rejected: e.target.checked }), 0);
                            }}
                            className="w-4 h-4 accent-[#FF3B30] cursor-pointer"
                          />
                        </label>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 justify-end">
                          {p.rejected && (
                            <button
                              onClick={() => setEvidenceOpen(evidenceOpen === p.id ? null : p.id)}
                              data-testid="toggle-evidence"
                              className="p-1 rounded-sm border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors"
                              title="Evidencia"
                            >
                              <Camera size={12} />
                              {p.evidence?.length > 0 && (
                                <span className="ml-1 font-mono-data text-[10px]">
                                  {p.evidence.length}
                                </span>
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => deleteRow(p.id)}
                            data-testid="delete-row"
                            className="p-1 rounded-sm border border-white/10 text-white/60 hover:text-[#FF3B30] hover:border-[#FF3B30]/40 transition-colors"
                          >
                            <Trash size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {p.rejected && evidenceOpen === p.id && (
                      <tr className="bg-black/30 border-b border-white/10">
                        <td colSpan={11} className="p-4">
                          <EvidenceZone
                            appointmentId={id}
                            product={p}
                            onChanged={loadAll}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/40 mb-1">
        {label}
      </div>
      <div className="text-white/90 font-medium">{value}</div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const toneCls = tone === "error" ? "text-[#FF3B30]" : "text-white";
  return (
    <div className="bg-[#111111] border border-white/10 rounded-sm p-4">
      <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/40 mb-1">
        {label}
      </div>
      <div className={`font-display text-2xl ${toneCls}`}>{value}</div>
    </div>
  );
}

function Cell({ v, onChange, onBlur, type = "text", alignRight }) {
  return (
    <td className={`px-2 py-1 ${alignRight ? "text-right" : ""}`}>
      <input
        type={type}
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={`w-full bg-transparent border border-transparent focus:border-[#0055FF] focus:bg-black/40 rounded-sm px-2 py-1 text-xs font-mono-data text-white transition-colors outline-none ${
          alignRight ? "text-right" : ""
        }`}
      />
    </td>
  );
}

function EvidenceZone({ appointmentId, product, onChanged }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [reason, setReason] = useState(product.rejection_reason || "");

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const token = localStorage.getItem("rcn_token");
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await fetch(
          `${API}/appointments/${appointmentId}/products/${product.id}/evidence`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          }
        );
        if (!res.ok) throw new Error("Error subiendo archivo");
      }
      toast.success(`${files.length} archivo(s) subido(s)`);
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  const removeEvidence = async (eid) => {
    if (!window.confirm("¿Eliminar evidencia?")) return;
    try {
      await api.delete(
        `/appointments/${appointmentId}/products/${product.id}/evidence/${eid}`
      );
      toast.success("Eliminado");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveReason = async () => {
    try {
      await api.put(`/appointments/${appointmentId}/products/${product.id}`, {
        ...product,
        rejection_reason: reason,
      });
      toast.success("Motivo actualizado");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const token = localStorage.getItem("rcn_token");
  return (
    <div className="border border-[#FF3B30]/30 rounded-sm p-4 bg-[#FF3B30]/5">
      <div className="flex items-center gap-2 mb-3">
        <Warning size={16} className="text-[#FF3B30]" weight="fill" />
        <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#FF3B30]">
          Evidencia de rechazo · {product.sku || "sin SKU"}
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
          Motivo del rechazo
        </label>
        <div className="flex gap-2">
          <input
            data-testid="rejection-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Producto dañado, empaque roto…"
            className="flex-1 px-3 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
          />
          <button
            data-testid="save-reason-btn"
            onClick={saveReason}
            className="px-3 py-2 rounded-sm bg-white/5 border border-white/10 text-xs text-white/80 hover:bg-white/10 transition-colors"
          >
            Guardar
          </button>
        </div>
      </div>

      <div className="mb-3 flex items-center gap-2">
        <button
          onClick={() => fileRef.current?.click()}
          data-testid="upload-evidence-btn"
          disabled={uploading}
          className="flex items-center gap-2 px-3 py-2 rounded-sm bg-[#FF3B30] text-white text-xs font-semibold hover:brightness-110 disabled:opacity-50 transition-transform active:scale-[0.98]"
        >
          <Upload size={14} />
          {uploading ? "Subiendo…" : "Subir foto / archivo"}
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => {
            upload(e.target.files);
            e.target.value = "";
          }}
        />
        <div className="text-[10px] text-white/40">JPG, PNG, PDF · hasta 20MB c/u</div>
      </div>

      {product.evidence?.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {product.evidence.map((ev) => {
            const isImage = (ev.content_type || "").startsWith("image/");
            const src = `${API}/evidence/${ev.path}`;
            return (
              <div
                key={ev.id}
                className="relative group border border-white/10 rounded-sm overflow-hidden bg-[#0A0A0A] aspect-square"
              >
                {isImage ? (
                  <img
                    src={`${src}?t=${token}`}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // fallback: fetch with auth header
                      fetch(src, { headers: { Authorization: `Bearer ${token}` } })
                        .then((r) => r.blob())
                        .then((b) => (e.target.src = URL.createObjectURL(b)))
                        .catch(() => {});
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-white/40">
                    <ImageSquare size={24} />
                    <div className="text-[9px] mt-1 px-1 truncate max-w-full">
                      {ev.filename}
                    </div>
                  </div>
                )}
                <button
                  onClick={() => removeEvidence(ev.id)}
                  className="absolute top-1 right-1 p-1 rounded-sm bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash size={10} />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="border border-dashed border-white/10 rounded-sm p-4 text-center text-[11px] text-white/40">
          Sin evidencia subida
        </div>
      )}
    </div>
  );
}

function safeDate(iso) {
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Mexico_City",
    });
  } catch (e) {
    return iso;
  }
}
